import type { CampanaActualizarInput, CampanaPublic } from '@app/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/ui/form-field';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { campanasApi } from '../api/campanas.api';

interface Props {
  campana: CampanaPublic;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const aInputDate = (iso: string | null): string => (iso ? iso.slice(0, 10) : '');

// Editar una campaña ya creada: hoy solo hace falta corregir los textos (p. ej. Ailen
// cambió una plantilla en Notion) o cerrar la vigencia — nombre, tipo y números quedan
// fijos porque cambiarlos rompería la detección de leads ya sellados con esta campaña.
export function CampanaEditarDialog({ campana, open, onOpenChange }: Props): JSX.Element {
  const es = useI18n();
  const t = es.admin.campanas.editarDialogo;
  const queryClient = useQueryClient();
  const [textos, setTextos] = useState(campana.textos.join('\n\n'));
  const [hasta, setHasta] = useState(aInputDate(campana.vigenteHasta));
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: CampanaActualizarInput) => campanasApi.update(campana.id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.campanas.all });
      toast.success(t.exito);
      onOpenChange(false);
    },
    onError: (e) => {
      setError(getErrorMessage(e, es.errores.generico.algoSalioMal));
    },
  });

  function guardar(): void {
    setError(null);
    const lista = textos
      .split(/\n\s*\n/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    if (lista.some((s) => s.length < 3)) {
      setError(t.errorTextos);
      return;
    }
    const input: CampanaActualizarInput = {
      textos: lista,
      vigenteHasta: hasta ? new Date(`${hasta}T23:59:59-03:00`) : null,
    };
    mutation.mutate(input);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t.titulo} — {campana.nombre}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            guardar();
          }}
          className="flex flex-col gap-4"
          noValidate
        >
          {error && <Alert variant="error">{error}</Alert>}

          <FormField label={t.textos} htmlFor="campana-editar-textos" helper={t.textosAyuda}>
            <Textarea
              id="campana-editar-textos"
              rows={6}
              value={textos}
              onChange={(e) => setTextos(e.target.value)}
              className="text-sm"
            />
          </FormField>

          <FormField label={t.vigenteHasta} htmlFor="campana-editar-hasta">
            <Input
              id="campana-editar-hasta"
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </FormField>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t.cancelar}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t.guardando : t.guardar}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
